import React, { useState, useCallback, useMemo } from 'react';
import {
  Card, Stack, Group, Text, Button, Textarea, Badge,
  ThemeIcon, Progress, Divider, Alert
} from '@mantine/core';
import {
  IconCheck, IconPencil, IconRotateClockwise,
  IconSparkles, IconCompass, IconArrowRight,
  IconAlertTriangle, IconInfoCircle
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';
import { authFetch } from '../../utils/authFetch';

/**
 * BuilderNarrativeCard - Narrative card for Builder (use_case) assessments
 * Shows a read-only summary of use case context from step 1, then lets the user
 * explicitly trigger narrative generation. No redundant context form.
 */
export default function BuilderNarrativeCard({
  modelId,
  assessmentUuid,
  parentAssessmentUuid,
  bucket,
  path,
  businessContext,
  useCase,
  onApproved
}) {
  // Step: summary | generating | ready | approved | failed
  const [status, setStatus] = useState('summary');
  const [narrative, setNarrative] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [focusAreas, setFocusAreas] = useState([]);

  // Parse business context for summary display
  const contextSummary = useMemo(() => {
    if (!businessContext) return null;
    try {
      const parsed = JSON.parse(businessContext);
      if (typeof parsed === 'object' && parsed !== null) {
        // Template-based structured data — show as key-value pairs
        return { type: 'structured', data: parsed };
      }
    } catch {
      // Plain text description
    }
    return { type: 'text', data: businessContext };
  }, [businessContext]);

  // Generate Builder narrative using props directly (no local form state)
  const generateNarrative = useCallback(async () => {
    try {
      setStatus('generating');
      setKeywords([]);
      setNarrative('');

      console.log('[BuilderNarrativeCard] Generating Builder narrative with payload:', {
        modelId,
        assessmentUuid,
        parentAssessmentUuid,
        bucket,
        path,
        hasBusinessContext: !!businessContext,
        hasUseCase: !!useCase
      });

      const res = await authFetch('/.netlify/functions/builderGenerateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          parentAssessmentUuid,
          bucket,
          path,
          businessContext,
          useCase
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Generation failed');
        throw new Error(errorText || 'Request failed');
      }

      const data = await res.json();
      if (data.success && data.narrative) {
        setNarrative(data.narrative);
        setFocusAreas(data.focusAreas || []);

        const displayKeywords = (data.focusAreas || []).slice(0, 12)
          .map(a => a.replace(/([A-Z])/g, ' $1').trim());
        setKeywords(displayKeywords);
        setStatus('ready');
      } else {
        throw new Error(data.error || 'Narrative generation failed');
      }
    } catch (error) {
      console.error('[BuilderNarrativeCard] Generation error:', error);
      setStatus('failed');
      setNarrative('');
    }
  }, [modelId, assessmentUuid, parentAssessmentUuid, bucket, path, businessContext, useCase]);

  const handleGenerateClick = useCallback(() => {
    if (!modelId || !assessmentUuid) {
      console.error('[BuilderNarrativeCard] Missing modelId or assessmentUuid');
      return;
    }
    generateNarrative();
  }, [modelId, assessmentUuid, generateNarrative]);

  const handleApprove = () => {
    setStatus('approved');
    onApproved?.(narrative, focusAreas);
  };

  const handleRegenerate = () => {
    generateNarrative();
  };

  const statusBadge = (() => {
    switch (status) {
      case 'summary':
        return <Badge variant="light" color="orange">Review Context</Badge>;
      case 'generating':
        return (
          <Badge variant="light" color="orange">
            <Group gap="xs">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
              <Text size="sm">Generating...</Text>
            </Group>
          </Badge>
        );
      case 'ready':
        return <Badge variant="light" color="orange">Ready for Review</Badge>;
      case 'approved':
        return (
          <Badge variant="filled" color="orange">
            <Group gap="xs">
              <IconCheck size={14} />
              <Text size="sm">Approved</Text>
            </Group>
          </Badge>
        );
      case 'failed':
        return <Badge variant="light" color="red">Generation Failed</Badge>;
      default:
        return <Badge variant="outline" color="gray">Loading...</Badge>;
    }
  })();

  const borderColor = ({
    summary: '#fd7e14',
    generating: '#fd7e14',
    ready: '#fd7e14',
    approved: '#e8590c',
    failed: '#dc2626',
    loading: '#6b7280',
  })[status] || '#6b7280';

  const canEdit = status === 'ready' || status === 'approved';
  const canApprove = status === 'ready' || status === 'approved';
  const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 50 : (status === 'summary' ? 10 : 25)));

  return (
    <Stack gap="lg">
      {/* Header with Progress */}
      <Card withBorder p="lg" style={{ backgroundColor: 'rgba(253, 126, 20, 0.05)' }}>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'orange', to: 'yellow' }}>
                  <IconCompass size={24} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={600} size="lg">Builder Risk Analysis</Text>
                  <Text size="sm" c="dimmed">
                    Assessing use-case deployment risks
                  </Text>
                </Stack>
              </Group>
            </div>
            {statusBadge}
          </Group>
          <Progress value={progress} size="sm" color="orange" />
        </Stack>
      </Card>

      {/* Use Case Context Summary (read-only) */}
      {status === 'summary' && (
        <Card withBorder p="lg" style={{ borderColor: '#fd7e14' }}>
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon size="lg" variant="light" color="orange">
                <IconInfoCircle size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600}>Use Case Context Summary</Text>
                <Text size="sm" c="dimmed">
                  Review the context from step 1 before generating the risk narrative
                </Text>
              </Stack>
            </Group>

            <Divider />

            {contextSummary?.type === 'structured' ? (
              <Stack gap="xs">
                {Object.entries(contextSummary.data)
                  .filter(([, value]) => value && String(value).trim())
                  .map(([key, value]) => (
                    <Group key={key} gap="sm" align="flex-start" wrap="nowrap">
                      <Text size="sm" fw={500} c="dimmed" style={{ minWidth: 140 }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}:
                      </Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Text>
                    </Group>
                  ))}
              </Stack>
            ) : contextSummary?.type === 'text' ? (
              <Card withBorder p="md" bg="gray.0">
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {contextSummary.data}
                </Text>
              </Card>
            ) : (
              <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">No use case context was provided in step 1.</Text>
              </Alert>
            )}

            <Group justify="flex-end" mt="md">
              <Button
                color="orange"
                rightSection={<IconArrowRight size={16} />}
                onClick={handleGenerateClick}
              >
                Generate Narrative
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Technical Risks (inherited) panel removed — Builder assessments are
          standalone and don't inherit from a parent. The legacy plumbing for
          parent-Builder dependency was retired with the form-based flow. */}

      {/* Focus Areas */}
      {status !== 'summary' && focusAreas.length > 0 && (
        <Card withBorder p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>Suggested Focus Areas</Text>
            <Group gap="xs">
              {focusAreas.map((area, idx) => (
                <Badge key={idx} variant="light" color="orange" size="sm">
                  {area.replace(/([A-Z])/g, ' $1').replace('Risk', '').trim()}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Card>
      )}

      {/* Narrative Content */}
      {status !== 'summary' && (
      <Card withBorder p="lg" style={{ borderColor }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconSparkles size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Risk Narrative</Text>
            </Group>
            {canEdit && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel Edit' : 'Edit'}
              </Button>
            )}
          </Group>

          {status === 'generating' && (
            <Stack align="center" py="xl">
              <AIGeneratingIcon isGenerating size={48} />
              <Text size="sm" c="dimmed">Analyzing use case context...</Text>
            </Stack>
          )}

          {status === 'failed' && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Stack gap="xs">
                <Text size="sm">Failed to generate narrative. Please try again.</Text>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  onClick={handleRegenerate}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          )}

          {(status === 'ready' || status === 'approved') && (
            <>
              {isEditing ? (
                <Textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  minRows={10}
                  autosize
                />
              ) : (
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {narrative}
                </Text>
              )}

              {/* Keywords */}
              {keywords.length > 0 && (
                <>
                  <Divider />
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Keywords:</Text>
                    {keywords.map((kw, idx) => (
                      <Badge key={idx} size="xs" variant="outline" color="orange">
                        {kw}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}

              {/* Actions */}
              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconRotateClockwise size={16} />}
                  onClick={handleRegenerate}
                  disabled={status === 'approved'}
                >
                  Regenerate
                </Button>
                {canApprove && status !== 'approved' && (
                  <Button
                    color="orange"
                    size="sm"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                  >
                    Approve & Continue
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Card>
      )}
    </Stack>
  );
}
